import { createSlice } from '@reduxjs/toolkit';
import {
  CleanupAction,
  EquipmentInterfacesState,
  EquipmentInterfaceState,
  EquipmentInterfacingActionState,
  ReadAction,
  ReadCancelAction,
  ReadEmitAction,
  ReadRejectAction,
  ReadResolveAction,
  RequestInterfaceState,
  RequestPortAction,
  RequestPortRejectAction,
  RequestPortResolveAction,
  RequestValueAction,
  RequestValueCleanupAction,
  RequestValueRejectAction,
  RequestValueResolveAction,
  SendAction,
  SendRejectAction,
  SendResolveAction,
  UnregisterPortAction,
  UnregisterPortRejectAction,
  UnregisterPortResolveAction,
} from './types';
import { hasNonEmptyValue } from 'Lib/Any';
import { last } from 'ramda';
import { WritableDraft } from 'immer/dist/internal';

const name = 'equipmentInterfacing';

const initialState: EquipmentInterfacesState = {
  equipment: {},
  requests: {},
  isConnecting: false,
};
const getInitialInterfacingActionState = (): EquipmentInterfacingActionState => ({
  isInProgress: false,
  complete: false,
  failed: false,
  error: undefined,
});
const getInitialEquipmentState = (): EquipmentInterfaceState => ({
  isConnected: false,
  connect: getInitialInterfacingActionState(),
  disconnect: getInitialInterfacingActionState(),
});
const getInitialRequestState = (): RequestInterfaceState => ({
  logItems: [],
  read: getInitialInterfacingActionState(),
  send: getInitialInterfacingActionState(),
  receive: getInitialInterfacingActionState(),
  obtainValue: getInitialInterfacingActionState(),
});

const initialiseEquipmentStateIfEmpty = (state: WritableDraft<EquipmentInterfacesState>, equipmentId: string) => {
  if (!hasNonEmptyValue(state.equipment[equipmentId])) state.equipment[equipmentId] = getInitialEquipmentState();
};
const initialiseRequestStateIfEmpty = (state: WritableDraft<EquipmentInterfacesState>, requestId: string) => {
  if (!hasNonEmptyValue(state.requests[requestId])) state.requests[requestId] = getInitialRequestState();
};

export const { reducer, actions } = createSlice({
  name,
  initialState,
  reducers: {
    requestPort: (state, action: RequestPortAction) => {
      const {
        payload: { equipmentId },
      } = action;

      initialiseEquipmentStateIfEmpty(state, equipmentId);
      state.equipment[equipmentId].isConnected = false;
      state.equipment[equipmentId].connect.isInProgress = true;
      state.equipment[equipmentId].connect.complete = false;
      state.equipment[equipmentId].connect.failed = false;
      state.equipment[equipmentId].connect.error = undefined;

      state.isConnecting = true;
      state.connectionDetails = action.payload;
    },

    requestPortResolve: (state, action: RequestPortResolveAction) => {
      const {
        payload: { equipmentId },
      } = action;

      initialiseEquipmentStateIfEmpty(state, equipmentId);
      state.equipment[equipmentId].isConnected = true;
      state.equipment[equipmentId].connect.isInProgress = false;
      state.equipment[equipmentId].connect.complete = true;
      state.equipment[equipmentId].connect.failed = false;
      state.equipment[equipmentId].connect.error = undefined;

      state.isConnecting = Object.values(state.equipment).some((e) => e.connect?.isInProgress);
    },

    requestPortReject: (state, action: RequestPortRejectAction) => {
      const {
        payload: { equipmentId, error },
      } = action;

      initialiseEquipmentStateIfEmpty(state, equipmentId);
      state.equipment[equipmentId].isConnected = false;
      state.equipment[equipmentId].connect.isInProgress = false;
      state.equipment[equipmentId].connect.complete = false;
      state.equipment[equipmentId].connect.failed = true;
      state.equipment[equipmentId].connect.error = error;

      state.isConnecting = Object.values(state.equipment).some((e) => e.connect?.isInProgress);
    },

    unregisterPort: (state, action: UnregisterPortAction) => {
      const {
        payload: { equipmentId },
      } = action;

      initialiseEquipmentStateIfEmpty(state, equipmentId);
      state.equipment[equipmentId].disconnect.isInProgress = true;
      state.equipment[equipmentId].disconnect.complete = false;
      state.equipment[equipmentId].disconnect.failed = false;
      state.equipment[equipmentId].disconnect.error = undefined;
    },

    unregisterPortResolve: (state, action: UnregisterPortResolveAction) => {
      const {
        payload: { equipmentId },
      } = action;

      initialiseEquipmentStateIfEmpty(state, equipmentId);
      state.equipment[equipmentId].isConnected = false;

      state.equipment[equipmentId].disconnect.isInProgress = false;
      state.equipment[equipmentId].disconnect.complete = true;
      state.equipment[equipmentId].disconnect.failed = false;
      state.equipment[equipmentId].disconnect.error = undefined;
    },

    unregisterPortReject: (state, action: UnregisterPortRejectAction) => {
      const {
        payload: { equipmentId, error },
      } = action;

      initialiseEquipmentStateIfEmpty(state, equipmentId);
      state.equipment[equipmentId].disconnect.isInProgress = false;
      state.equipment[equipmentId].disconnect.complete = false;
      state.equipment[equipmentId].disconnect.failed = true;
      state.equipment[equipmentId].disconnect.error = error;
    },

    cleanupEquipment: (state, action: CleanupAction) => {
      const {
        payload: { equipmentId },
      } = action;
      delete state.equipment[equipmentId];
    },

    read: (state, action: ReadAction) => {
      const {
        payload: { requestId },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].read.isInProgress = true;
      state.requests[requestId].read.complete = false;
      state.requests[requestId].read.failed = false;
      state.requests[requestId].read.error = undefined;
    },

    readReady: (state, action: ReadResolveAction) => {},

    readResolve: (state, action: ReadResolveAction) => {
      const {
        payload: { requestId },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].read.isInProgress = false;
      state.requests[requestId].read.complete = true;
      state.requests[requestId].read.failed = false;
      state.requests[requestId].read.error = undefined;
      state.requests[requestId].receive.receivedValue = undefined;
    },

    readReject: (state, action: ReadRejectAction) => {
      const {
        payload: { requestId, error },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].read.isInProgress = false;
      state.requests[requestId].read.complete = false;
      state.requests[requestId].read.failed = true;
      state.requests[requestId].read.error = error;
    },

    send: (state, action: SendAction) => {
      const {
        payload: { requestId },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].send.isInProgress = true;
      state.requests[requestId].send.complete = false;
      state.requests[requestId].send.failed = false;
      state.requests[requestId].send.error = undefined;
    },

    sendResolve: (state, action: SendResolveAction) => {
      const {
        payload: { requestId, value },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].send.isInProgress = false;
      state.requests[requestId].send.complete = true;
      state.requests[requestId].send.failed = false;
      state.requests[requestId].logItems.push({ type: 'Sent', value: value });
    },

    sendReject: (state, action: SendRejectAction) => {
      const {
        payload: { requestId, error },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].send.isInProgress = false;
      state.requests[requestId].send.complete = false;
      state.requests[requestId].send.failed = true;
      state.requests[requestId].send.error = error;
    },

    readCancel: (state, action: ReadCancelAction) => {},

    readEmit: (state, action: ReadEmitAction) => {
      const {
        payload: { requestId, value },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].receive.receivedValue = `${state.requests[requestId].receive.receivedValue ?? ''}${
        value ?? ''
      }`;

      let lastLogItem = last(state.requests[requestId].logItems);
      if (lastLogItem?.type === 'Received') {
        lastLogItem.value = `${lastLogItem.value ?? ''}${value ?? ''}`;
      } else {
        state.requests[requestId].logItems?.push({ type: 'Received', value: value });
      }
    },

    obtainValue: (state, action: RequestValueAction) => {
      const {
        payload: { requestId },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].logItems = [];
      state.requests[requestId].obtainValue.isInProgress = true;
      state.requests[requestId].obtainValue.complete = false;
      state.requests[requestId].obtainValue.failed = false;
      delete state.requests[requestId].obtainValue.error;
      delete state.requests[requestId].obtainValue.obtainedValue;
    },

    obtainValueResolve: (state, action: RequestValueResolveAction) => {
      const {
        payload: { requestId, value },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].obtainValue.isInProgress = false;
      state.requests[requestId].obtainValue.complete = true;
      state.requests[requestId].obtainValue.failed = false;
      state.requests[requestId].obtainValue.obtainedValue = value;
    },

    obtainValueReject: (state, action: RequestValueRejectAction) => {
      const {
        payload: { requestId, error },
      } = action;

      initialiseRequestStateIfEmpty(state, requestId);
      state.requests[requestId].obtainValue.isInProgress = false;
      state.requests[requestId].obtainValue.complete = false;
      state.requests[requestId].obtainValue.failed = true;
      state.requests[requestId].obtainValue.error = error;
    },

    cleanupRequest: (state, action: RequestValueCleanupAction) => {
      const {
        payload: { requestId },
      } = action;

      delete state.requests[requestId];
    },
  },
});
